import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDevices,
  getDevice,
  provisionDevice,
  deprovisionDevice,
  bulkProvisionDevices,
} from '../services/deviceService';
import type { CreateDeviceRequest, BulkProvisionRequest } from '../types/device';

export function useDevices(customerId?: string) {
  return useQuery({
    queryKey: ['devices', customerId],
    queryFn: () => getDevices(customerId),
  });
}

export function useDevice(deviceId: string) {
  return useQuery({
    queryKey: ['devices', 'detail', deviceId],
    queryFn: () => getDevice(deviceId),
    enabled: !!deviceId,
  });
}

export function useProvisionDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDeviceRequest) => provisionDevice(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['customers', variables.customer_id] });
    },
  });
}

export function useBulkProvisionDevices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkProvisionRequest) => bulkProvisionDevices(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['customers', variables.customer_id] });
    },
  });
}

export function useDeprovisionDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deviceId: string) => deprovisionDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}
